<template>
  <template v-if="question?.status !== 'READ_ONLY'">
    <div>
      <Datepicker
          v-bind="$attrs"
          format="MM/dd/yyyy"
          :name="question.name"
          :id="question.name"
          ref="calendarPicker"
          input-class="internalInput fw-bold"
          v-maska="'##/##/####'"
          v-model="dateInput"
          @update:modelValue="dateSelected"
          class="outline w-100 mx-0 px-0"
          :class="{
            'invalid':error && hasBeenInteractedWith,
            'placeholder-adjusment': isCurrentlyFocused || dateInput}"
          :typeable="true"
          :calendar-button="true"
          :calendar-button-icon="'bi calendar-icon'"
          :full-month-name="true"
          :disabled="disabled"
          :disabledDates="availableDates"
          :preventDisableDateSelection="true"
          :open-date="
        dateInput ||
        (availableDates.from && availableDates.from < new Date()
          ? availableDates.from
          : new Date())
      "
          @focusin="isCurrentlyFocused = true; $event.target.setSelectionRange(dateInput?.length, dateInput?.length);"
          @focusout="isCurrentlyFocused = false; $emit('user-interaction')"
      >
      </Datepicker>
      <span class="quill-question-placeholder placeholder-wrap">
      {{ forcePromptPlaceholder ? question.prompt : (question.placeholder || question.prompt) }}
    </span>
    </div>
    <slot name="helptext" :isError="error" :isConfirm="!disableConfirm && (question?.value && question?.prefilled) && !error"
          :confirmCb="() => { $emit('user-interaction'); $emit('confirm');}"></slot>
  </template>
  <ReadOnly
      v-else
      :question="question"
      :value="dateInput ? (typeof dateInput !== 'string' ? momentFormat(dateInput).format('MM/DD/yyyy'):momentFormat(dateInput,'YYYYMMDD').format('MM/DD/yyyy')):'-'"
      :forcePromptPlaceholder="forcePromptPlaceholder"
  />
</template>

<script>
import Datepicker from "vuejs3-datepicker";
import moment from "moment";
import ReadOnly from "@/components/waffle/components/form-components/ReadOnly.vue";

export default {
  name: "CalendarDatePicker",
  emits: ["update:modelValue", "field-error",'confirm','user-interaction'],
  props: {
    disableConfirm:Boolean,
    question: {
      type: Object,
      required: true,
    },
    hideSubText: {
      type: Boolean,
    },
    skipEagerValidation: {
      type: Boolean,
    },
    forcePromptPlaceholder: {
      type: Boolean,
    },
    hasBeenInteractedWith:Boolean,
    disabled: Boolean
  },
  components: {
    ReadOnly,
    Datepicker,
  },
  data() {
    return {
      dateInput: "",
      dateRange:
          this.question?.criteria?.find((el) => typeof el === "object")?.[
              "range"
              ] ?? [],
      error: "",
      isCurrentlyFocused: false,
    };
  },
  mounted() {
    const calendarInput = this.$refs.calendarPicker?.$el.querySelector('.internalInput')
    if(calendarInput){
      calendarInput.inputMode = 'numeric';
    }

    if (this.question.value) {
      this.dateInput = moment(this.question.value, "YYYYMMDD").toDate();
    }
    if (!this.skipEagerValidation) {
      this.error = this.question.error;
      if (!this.error && this.question?.required && !this.dateInput) {
        this.error = "Required";
      }
      if (this.error) {
        this.$emit("field-error", true);
      }
      // else {
      //   this.$emit("field-error", false);
      // }
    }
  },
  watch: {
    question: {
      handler(newQuestion) {
        if (newQuestion.error) {
          this.error = newQuestion.error;
          return this.$emit("field-error", true);
        }
        if (newQuestion.value && newQuestion.value !== moment(this.dateInput).format("YYYYMMDD")) {
          this.dateSelected(moment(newQuestion.value, "YYYYMMDD").toDate())
        }
      },
      deep: true,
    },
  },
  computed: {
    momentFormat(){
      return moment
    },
    availableDates() {
      return {
        from: this.dateRange[1] ? new Date(this.dateRange[1]?.split('T')[0]) : null, //max
        to: this.dateRange[0]  ? new Date(this.dateRange[0]?.split('T')[0]) : null, //min
      };
    },
  },
  methods: {
    //jest param is here due to mocking of Datepicker
    dateSelected(payload, jest=false) {
      this.$nextTick(() => {
        this.dateInput = payload;
        const trueInputValue = document.querySelector(`#${this.question.name}`)?.value

        if(!jest && !trueInputValue?.split('/')?.every((el, i) => (el.length === 2 && i < 2) || (el.length === 4 && i === 2 && el !== '0000'))){
          this.error="Invalid Date"
          return this.$emit("field-error", true);
        }

        if (!payload && this.question.required) {
          this.error = "Required";
          return this.$emit("field-error", true);
        }
        const selectedDate = moment(payload);
        if (
            this.dateRange.length &&
            !selectedDate.isBetween(
                this.availableDates.to,
                this.availableDates.from
            )
        ) {
          this.error = "Not a valid date";
          return this.$emit("field-error", true);
        }

        this.error = "";

        this.$emit("field-error", false);
        this.$emit('user-interaction');
        return this.$emit("update:modelValue", selectedDate.format("YYYYMMDD"));
      })
    },
  },
};
</script>

<style scoped lang="scss">
.internalInput::placeholder { text-transform: uppercase !important; }

.align-text-end {
  text-align: end;
}

.outline {
  border: 1px solid #bdbdbd;
  border-radius: 3px;

  $primary-color: #212121;
  $primary-font: 'Roboto', sans-serif;
  $secondary-font: 'Lato', sans-serif;

  /**Modal Dialog CSS END*/
  :deep(.vuejs3-datepicker__inputvalue) {
    width: 100% !important;
    border: none !important;
    font-family: $primary-font;

    &::placeholder {
      text-transform: lowercase;
    }
  }

  :deep(.vuejs3-datepicker__inputvalue) {
    // padding: 12px 10px 13px 12px;
    padding: 25px 8px 5px 12px;

    &:focus {
      outline: none !important;
    }
  }

  :deep(.vuejs3-datepicker__calendar header span) {
    font-weight: bold;
    font-size: 16px;
    text-transform: uppercase;
  }

  :deep(.vuejs3-datepicker__calendar-topbar),
  :deep(.vuejs3-datepicker__typeablecalendar) {
    display: none;
  }

  :deep(.vuejs3-datepicker__calendar) {
    background: #f8f8f8;
    color: $primary-color;
    z-index: 96;
    width: 100%;
    border-radius: 10px;
    border: 1px solid #f8f8f8;
    box-shadow: 0px 0px 6px #5f5d5d29;
    margin-top: 3px;
  }

  :deep(.vuejs3-datepicker__calendar .cell.day-header) {
    color: #243849;
  }

  :deep(.vuejs3-datepicker__calendar .cell.selected) {
    background: inherit!important;
    position: relative;
  }

  :deep(.vuejs3-datepicker__calendar .cell.selected:before) {
    background: $primary-color!important;
    position: absolute;
    content: '';
    width: 35px;
    border-radius: 50%;
    aspect-ratio: 1/1;
    left: calc(50% - 18px);
    top: calc(50% - 18px);
    z-index: -1;
  }

  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover),
  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover),
  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover) {
    border: none;
  }

  :deep(.vuejs3-datepicker__calendar .cell.selected:hover) {
    background: $primary-color;
  }

  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).day),
  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).month),
  :deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).year) {
    transition: none;
  }

  :deep(.vuejs3-datepicker__calendar-button) {
    position: absolute;
    z-index: 1;
    top: 16px;
    width: 16px;
  }

  :deep(.calendar-icon) {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath d='M3.5,0A.5.5,0,0,1,4,.5V1h8V.5a.5.5,0,0,1,1,0V1h1a2,2,0,0,1,2,2V14a2,2,0,0,1-2,2H2a2,2,0,0,1-2-2V3A2,2,0,0,1,2,1H3V.5A.5.5,0,0,1,3.5,0ZM2,2A1,1,0,0,0,1,3V4H15V3a1,1,0,0,0-1-1ZM15,5H1v9a1,1,0,0,0,1,1H14a1,1,0,0,0,1-1Z'/%3E%3Cpath d='M11,7.5a.5.5,0,0,1,.5-.5h1a.5.5,0,0,1,.5.5v1a.5.5,0,0,1-.5.5h-1a.5.5,0,0,1-.5-.5Z'/%3E%3C/svg%3E");
    height: 16px;
    width: 16px;
    display: block;
  }
}

.invalid {
  border: 1px solid #bb0000;
  border-radius: 3px;

  &:hover,
  &:focus,
  &:focus-within {
    border: 2px solid #bb0000 !important;
  }
}


.subtext {
  font-size: .75rem;
  font: normal normal normal 12px/18px Roboto;
  letter-spacing: 0px;
  margin-bottom: 4px;
  color: #757575
}

.errorMessage {
  color: #bb0000;
}

.quill-question-placeholder {
  position: absolute;
  padding: 12px 16px;
  padding-right:32px !important;
  line-height: 2em;
  left:0;
  color: #757575;
  font-size: 16px;
  pointer-events: none;
  transform: scale(1);
  transition: .26s all ease;
}
.placeholder-adjusment + .quill-question-placeholder {
  font-size: 12px !important;
  padding-top:6px !important;
  padding-bottom:12px !important;
  padding-left:12px ;
}
</style>
